import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  Timestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { DATABASE } from "../../../../Config/firebase";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
  CRYPTO_ASSETS,
  CRYPTO_NETWORKS,
  WALLET_PROVIDERS,
  PROVIDER_COLORS,
  maskAddress,
  buildWalletEditChanges,
  buildWalletReference,
  getWalletCooldownUntil,
  MAX_EDIT_HISTORY_ENTRIES,
  WALLET_TYPES,
} from "../../../../Utils/cryptoWalletUtils";
import { normalizeAddressForCompare } from "../../../../Utils/walletQr";

const formatWalletTimestamp = (ts) => {
  if (!ts) return null;
  const m = ts?.toDate ? moment(ts.toDate()) : ts?.seconds ? moment(ts.seconds * 1000) : null;
  return m?.isValid() ? m.format("DD MMM YYYY, HH:mm") : null;
};

const formatHistoryChange = (changes) =>
  Object.entries(changes || {})
    .map(([key, val]) => {
      if (key === "is_default") {
        return `${key}: ${val.from ? "yes" : "no"} → ${val.to ? "yes" : "no"}`;
      }
      return `${key}: ${val.from} → ${val.to}`;
    })
    .join(" · ");

const formatChangeSummary = (changes) =>
  Object.entries(changes)
    .map(([key, val]) => {
      if (key === "is_default") {
        return `<b>${key}</b>: ${val.from ? "Yes" : "No"} → ${val.to ? "Yes" : "No"}`;
      }
      return `<b>${key}</b>: ${val.from} → ${val.to}`;
    })
    .join("<br/>");

/** MUI Dialog uses z-index 1300; SweetAlert2 default is lower — raise so confirms appear on top. */
const swalAboveDialog = (options) =>
  Swal.fire({
    ...options,
    didOpen: (popup) => {
      const container = popup?.parentElement || document.querySelector(".swal2-container");
      if (container) container.style.zIndex = "1400";
      if (typeof options.didOpen === "function") options.didOpen(popup);
    },
  });

const EditWalletModal = ({ open, onClose, onSuccess, walletRow }) => {
  const [label, setLabel] = useState("");
  const [asset, setAsset] = useState("USDT");
  const [network, setNetwork] = useState("POLYGON");
  const [provider, setProvider] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addressRevealed, setAddressRevealed] = useState(false);
  const [walletDetail, setWalletDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [proofConfirmation, setProofConfirmation] = useState(null);

  useEffect(() => {
    if (walletRow) {
      setLabel(walletRow.label || "");
      setAsset(walletRow.asset || "USDT");
      setNetwork(walletRow.network || "POLYGON");
      setProvider(walletRow.provider || "");
      setIsDefault(!!walletRow.is_default);
      setAddressRevealed(false);
    }
  }, [walletRow]);

  useEffect(() => {
    if (!open || !walletRow?.user_id || !walletRow?.wallet_doc_id) {
      setWalletDetail(null);
      return;
    }
    let cancelled = false;
    setLoadingDetail(true);
    getDoc(doc(DATABASE, "users", walletRow.user_id, "crypto_wallets", walletRow.wallet_doc_id))
      .then((snap) => {
        if (!cancelled && snap.exists()) setWalletDetail(snap.data());
      })
      .catch((err) => console.warn("Failed to load wallet detail", err))
      .finally(() => { if (!cancelled) setLoadingDetail(false); });
    return () => { cancelled = true; };
  }, [open, walletRow?.user_id, walletRow?.wallet_doc_id]);

  // Proof photo from Wallet Check confirmations (read-only) for this wallet
  useEffect(() => {
    if (!open || !walletRow?.user_id) {
      setProofConfirmation(null);
      return;
    }
    let cancelled = false;
    getDocs(collection(DATABASE, "users", walletRow.user_id, "wallet_confirmations"))
      .then((snap) => {
        if (cancelled) return;
        const incoming = normalizeAddressForCompare(walletRow.address, walletRow.network);
        const matches = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter(
            (c) =>
              c.photo_url &&
              ((c.matched_wallet_id && c.matched_wallet_id === walletRow.wallet_id) ||
                normalizeAddressForCompare(c.address, c.network) === incoming)
          )
          .sort((a, b) => (b.confirmed_at?.seconds || 0) - (a.confirmed_at?.seconds || 0));
        setProofConfirmation(matches[0] || null);
      })
      .catch(() => {
        if (!cancelled) setProofConfirmation(null);
      });
    return () => { cancelled = true; };
  }, [open, walletRow?.user_id, walletRow?.wallet_doc_id, walletRow?.address, walletRow?.network, walletRow?.wallet_id]);

  const handleClose = () => { setAddressRevealed(false); onClose(); };

  const getPendingChanges = () => {
    const trimmedLabel = label.trim() || `${asset} Wallet`;
    return buildWalletEditChanges(
      {
        label: walletRow?.label,
        asset: walletRow?.asset,
        network: walletRow?.network,
        provider: walletRow?.provider,
        is_default: walletRow?.is_default,
      },
      {
        label: trimmedLabel,
        asset,
        network,
        provider,
        is_default: isDefault,
      }
    );
  };

  const handleSubmit = async () => {
    if (!walletRow?.user_id || !walletRow?.wallet_doc_id) return;

    const changes = getPendingChanges();
    if (Object.keys(changes).length === 0) {
      toast.info("No changes to save");
      return;
    }

    const result = await swalAboveDialog({
      title: "Confirm wallet changes",
      html: `
        <p style="text-align:left;margin-bottom:12px;">
          Auto-pay for this wallet will be <strong>suspended for 24 hours</strong> after saving.
          Admins will be notified by email.
        </p>
        <div style="text-align:left;font-size:14px;">${formatChangeSummary(changes)}</div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2af598",
      cancelButtonColor: "#5b5d5d",
      confirmButtonText: "Confirm changes",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    setSaving(true);
    try {
      const userId = walletRow.user_id;
      const walletDocId = walletRow.wallet_doc_id;
      const adminEmail = localStorage.getItem("email") || "admin";
      const walletRef = doc(DATABASE, "users", userId, "crypto_wallets", walletDocId);
      const trimmedLabel = label.trim() || `${asset} Wallet`;
      const suspendedUntil = getWalletCooldownUntil();
      const now = Timestamp.now();

      const walletSnap = await getDoc(walletRef);
      const existingHistory = walletSnap.exists()
        ? walletSnap.data()?.edit_history || []
        : [];
      const historyEntry = {
        edited_at: now,
        edited_by: adminEmail,
        changes,
      };
      const nextHistory = [...existingHistory, historyEntry].slice(-MAX_EDIT_HISTORY_ENTRIES);

      const batch = writeBatch(DATABASE);

      if (isDefault && !walletRow.is_default) {
        const snap = await getDocs(collection(DATABASE, "users", userId, "crypto_wallets"));
        snap.docs.forEach((d) => {
          if (d.id !== walletDocId && d.data()?.is_default) {
            batch.update(doc(DATABASE, "users", userId, "crypto_wallets", d.id), {
              is_default: false,
            });
          }
        });
      }

      batch.update(walletRef, {
        label: trimmedLabel,
        asset,
        network,
        provider: provider || "",
        is_default: isDefault,
        last_edited_at: now,
        last_edited_by: adminEmail,
        edit_suspended_until: Timestamp.fromDate(suspendedUntil),
        edit_history: nextHistory,
      });

      await batch.commit();
      toast.success("Wallet updated — auto-pay suspended for 24h");
      onSuccess?.();
      onClose();
    } catch (e) {
      console.error(e);
      toast.error("Failed to update wallet");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    if (!walletRow?.user_id || !walletRow?.wallet_doc_id) return;
    const newActive = !walletRow.is_active;
    if (!newActive && walletRow.is_default) {
      toast.error("Cannot deactivate the default wallet. Set another wallet as default first.");
      return;
    }

    const actionLabel = newActive ? "activate" : "deactivate";
    const confirm = await swalAboveDialog({
      title: `${newActive ? "Activate" : "Deactivate"} wallet?`,
      text: newActive
        ? "This wallet will be available for client withdrawals again."
        : "This wallet will no longer appear in the client app.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;

    setSaving(true);
    try {
      const adminEmail = localStorage.getItem("email") || "admin";
      await updateDoc(
        doc(DATABASE, "users", walletRow.user_id, "crypto_wallets", walletRow.wallet_doc_id),
        {
          is_active: newActive,
          deactivated_date: newActive ? null : Timestamp.now(),
          deactivated_by: newActive ? null : adminEmail,
        }
      );
      toast.success(newActive ? "Wallet activated" : "Wallet deactivated");
      onSuccess?.();
      onClose();
    } catch (e) {
      console.error(e);
      toast.error("Failed to update wallet status");
    } finally {
      setSaving(false);
    }
  };

  const editHistory = useMemo(() => {
    const raw = walletDetail?.edit_history || [];
    return [...raw].reverse().slice(0, MAX_EDIT_HISTORY_ENTRIES);
  }, [walletDetail?.edit_history]);

  if (!walletRow) return null;

  const maskedAddr = maskAddress(walletRow.address, walletRow.network);
  const pendingChanges = getPendingChanges();
  const hasPendingChanges = Object.keys(pendingChanges).length > 0;
  const walletType = walletRow.wallet_type || WALLET_TYPES.WITHDRAWAL;
  const isWithdrawal = walletType === WALLET_TYPES.WITHDRAWAL;
  const now = Date.now();

  const autoPayEligibleFrom = walletRow.auto_pay_eligible_from || walletDetail?.auto_pay_eligible_from;
  const editSuspendedUntil = walletRow.edit_suspended_until || walletDetail?.edit_suspended_until;
  const autoPayBlocked = autoPayEligibleFrom?.toDate?.()
    ? now < autoPayEligibleFrom.toDate().getTime()
    : false;
  const editCooldownActive = editSuspendedUntil?.toDate?.()
    ? now < editSuspendedUntil.toDate().getTime()
    : false;

  const createdLabel = formatWalletTimestamp(walletDetail?.added_date);
  const addedBy = walletDetail?.added_by || walletRow.added_by;
  const walletReference = buildWalletReference(walletRow.client_name, walletRow.user_id);

  const captionSx = { color: "rgba(255,255,255,0.55)", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 };
  const valueSx = { color: "#fff", fontSize: 12, fontFamily: walletRow.wallet_id ? "inherit" : "monospace" };
  const monoValueSx = { ...valueSx, fontFamily: "monospace" };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { bgcolor: "#1a1a2e", backgroundImage: "none" } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Edit Wallet</Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", display: "block" }}>
              {walletRow.client_name} · {walletRow.client_email}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, mt: 0.5 }}>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", fontFamily: "monospace", fontSize: 11 }}>
                User ID: {walletRow.user_id}
              </Typography>
              <Tooltip title="Copy user ID">
                <IconButton
                  size="small"
                  onClick={() => { navigator.clipboard.writeText(walletRow.user_id); toast.info("User ID copied"); }}
                >
                  <ContentCopyIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", fontSize: 11 }}>
                Wallet reference: {walletReference}
              </Typography>
              <Tooltip title="Copy wallet reference (Bybit label)">
                <IconButton
                  size="small"
                  onClick={() => { navigator.clipboard.writeText(walletReference); toast.info("Wallet reference copied"); }}
                >
                  <ContentCopyIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
            <Chip
              size="small"
              label={isWithdrawal ? "Withdrawal" : "Deposit"}
              sx={{
                fontWeight: 700,
                fontSize: 10,
                bgcolor: isWithdrawal ? "rgba(211,47,47,0.2)" : "rgba(42,245,152,0.15)",
                color: isWithdrawal ? "#ff8a80" : "#2af598",
                border: `1px solid ${isWithdrawal ? "rgba(239,83,80,0.45)" : "rgba(42,245,152,0.35)"}`,
              }}
            />
            <Chip
              size="small"
              label={walletRow.is_active ? "Active" : "Inactive"}
              color={walletRow.is_active ? "success" : "default"}
            />
          </Box>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

          <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
            Saving changes suspends auto-pay to this wallet for <strong>24 hours</strong> and notifies admins by email.
            Wallet address cannot be changed.
          </Alert>

          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
              <Typography variant="caption" sx={captionSx}>Wallet ID</Typography>
              <Typography variant="caption" sx={monoValueSx}>
                {walletRow.wallet_id}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="caption" sx={captionSx}>Address (read-only)</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{ ...monoValueSx, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}
                >
                  {addressRevealed ? walletRow.address : maskedAddr}
                </Typography>
                <IconButton size="small" onClick={() => setAddressRevealed((p) => !p)}>
                  {addressRevealed
                    ? <VisibilityOffIcon sx={{ fontSize: 14 }} />
                    : <VisibilityIcon sx={{ fontSize: 14 }} />}
                </IconButton>
                {addressRevealed && (
                  <Tooltip title="Copy address">
                    <IconButton
                      size="small"
                      onClick={() => { navigator.clipboard.writeText(walletRow.address); toast.info("Address copied"); }}
                    >
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
            {(createdLabel || addedBy) && (
              <Typography variant="body2" sx={{ display: "block", mt: 1, color: "rgba(255,255,255,0.7)", fontSize: 12 }}>
                Created{createdLabel ? `: ${createdLabel}` : ""}{addedBy ? ` · by ${addedBy}` : ""}
              </Typography>
            )}
            {walletRow.last_edited_at && (
              <Typography variant="body2" sx={{ display: "block", mt: 0.5, color: "rgba(255,255,255,0.7)", fontSize: 12 }}>
                Last edited: {formatWalletTimestamp(walletRow.last_edited_at) || "—"} · {walletRow.last_edited_by || "—"}
              </Typography>
            )}
          </Box>

          {proofConfirmation && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: "rgba(0,0,0,0.2)",
                border: "1px solid rgba(42,245,152,0.25)",
              }}
            >
              <Typography variant="caption" sx={{ ...captionSx, display: "block", mb: 0.75 }}>
                Confirmation proof (from Wallet Check)
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                <Box
                  component="a"
                  href={proofConfirmation.photo_url}
                  target="_blank"
                  rel="noreferrer"
                  sx={{ flexShrink: 0 }}
                >
                  <Box
                    component="img"
                    src={proofConfirmation.photo_url}
                    alt="Wallet confirmation proof"
                    sx={{
                      width: 72,
                      height: 72,
                      objectFit: "cover",
                      borderRadius: 1,
                      border: "1px solid rgba(255,255,255,0.15)",
                    }}
                  />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)", fontSize: 12 }}>
                    Confirmed by {proofConfirmation.confirmed_by || "admin"}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.55)", fontSize: 11 }}>
                    {formatWalletTimestamp(proofConfirmation.confirmed_at) || "—"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ display: "block", color: "#7eb8ff", fontSize: 11, mt: 0.5 }}
                  >
                    Open full photo ↗
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              bgcolor: "rgba(0,0,0,0.2)",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <Typography variant="caption" sx={{ ...captionSx, display: "block", mb: 0.75 }}>
              Security status
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", fontSize: 12, lineHeight: 1.5 }}>
              Auto-pay:{" "}
              {autoPayEligibleFrom
                ? autoPayBlocked
                  ? `blocked until ${formatWalletTimestamp(autoPayEligibleFrom)}`
                  : "eligible"
                : "eligible (legacy wallet — no 24h create cooldown on record)"}
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", fontSize: 12, lineHeight: 1.5 }}>
              Edit cooldown:{" "}
              {editCooldownActive
                ? `auto-pay suspended until ${formatWalletTimestamp(editSuspendedUntil)}`
                : "none active"}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              bgcolor: "rgba(0,0,0,0.2)",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <Typography variant="caption" sx={{ ...captionSx, display: "block", mb: 0.75 }}>
              Edit history (on this wallet document)
            </Typography>
            {loadingDetail ? (
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.55)", fontSize: 12 }}>Loading…</Typography>
            ) : editHistory.length === 0 ? (
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.55)", fontSize: 12 }}>
                No edits recorded yet. Full audit trail also lives in Firestore collection{" "}
                <Box component="span" sx={{ fontFamily: "monospace", color: "rgba(255,255,255,0.7)" }}>
                  wallet_audit_log
                </Box>{" "}
                (admin emails on create/edit).
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, maxHeight: 140, overflowY: "auto" }}>
                {editHistory.map((entry, idx) => (
                  <Box key={idx} sx={{ borderLeft: "2px solid rgba(42,245,152,0.4)", pl: 1 }}>
                    <Typography variant="body2" sx={{ color: "#fff", fontSize: 12, fontWeight: 600 }}>
                      {formatWalletTimestamp(entry.edited_at) || "—"} · {entry.edited_by || "admin"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", fontSize: 11 }}>
                      {formatHistoryChange(entry.changes)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 1.5 }}>
            <TextField
              select label="Asset" value={asset}
              onChange={(e) => setAsset(e.target.value)}
              size="small" sx={{ flex: 1 }}
              InputLabelProps={{ sx: { color: "rgba(255,255,255,0.6)" } }}
              InputProps={{ sx: { color: "#fff" } }}
            >
              {CRYPTO_ASSETS.map((a) => (
                <MenuItem key={a} value={a}>{a}</MenuItem>
              ))}
            </TextField>
            <TextField
              select label="Network" value={network}
              onChange={(e) => setNetwork(e.target.value)}
              size="small" sx={{ flex: 1.4 }}
              InputLabelProps={{ sx: { color: "rgba(255,255,255,0.6)" } }}
              InputProps={{ sx: { color: "#fff" } }}
            >
              {CRYPTO_NETWORKS.map((n) => (
                <MenuItem key={n} value={n}>{n}</MenuItem>
              ))}
            </TextField>
          </Box>

          <TextField
            select
            label="Provider"
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            size="small"
            fullWidth
            helperText="Exchange or app that holds this address. Shown only in Wallet Management."
            InputLabelProps={{ sx: { color: "rgba(255,255,255,0.6)" } }}
            InputProps={{ sx: { color: "#fff" } }}
            FormHelperTextProps={{ sx: { color: "rgba(255,255,255,0.55)" } }}
          >
            <MenuItem value="">Not set</MenuItem>
            {WALLET_PROVIDERS.map((name) => (
              <MenuItem key={name} value={name}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: PROVIDER_COLORS[name]?.bg || "grey", border: "1px solid rgba(255,255,255,0.35)" }} />
                  {name}
                </Box>
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            fullWidth
            size="small"
            helperText={
              isWithdrawal
                ? "Shown to the client in the app when choosing a wallet, and in admin Withdrawals with the address suffix"
                : "Internal label for this Fiddux deposit address (not shown in app yet)"
            }
            InputLabelProps={{ sx: { color: "rgba(255,255,255,0.6)" } }}
            InputProps={{ sx: { color: "#fff" } }}
            FormHelperTextProps={{ sx: { color: "rgba(255,255,255,0.55)" } }}
          />

          {isWithdrawal && (
            <FormControlLabel
              control={
                <Switch
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                  disabled={!walletRow.is_active}
                  color="success"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ color: "#fff", fontWeight: 500 }}>Default wallet</Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.55)" }}>
                    Pre-selected in app during withdrawal
                  </Typography>
                </Box>
              }
            />
          )}
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
        <Button
          variant="outlined"
          color={walletRow.is_active ? "warning" : "success"}
          onClick={handleToggleActive}
          disabled={saving}
          size="small"
        >
          {walletRow.is_active ? "Deactivate" : "Activate"}
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={handleClose} variant="outlined" color="inherit">Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving || !hasPendingChanges}
            sx={{
              background: "linear-gradient(135deg, #2af598 0%, #00ba2d 100%)",
              color: "#000",
              fontWeight: 700,
              "&:hover": { background: "linear-gradient(135deg, #3adecc 0%, #2af598 100%)" },
            }}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EditWalletModal;
